"use client";
/*
 * Documentation:
 * DC_MetaIntro — https://app.subframe.com/4f62853ee923/library?component=DC_MetaIntro_75991cf2-d5b4-4e00-98d9-3b0edce8f0fd
 * DC_MetaIntro_ActionStack — https://app.subframe.com/4f62853ee923/library?component=DC_MetaIntro_ActionStack_c2a1f933-7ab4-4ec2-94fe-f4cfdc04d0e0
 * DC_Popover_Card_CiteAs — https://app.subframe.com/4f62853ee923/library?component=DC_Popover_Card_CiteAs_fc59b4a1-0da2-4f46-82c2-c74633d042c4
 * Icon Button — https://app.subframe.com/4f62853ee923/library?component=Icon+Button_af9405b1-8c54-4e01-9786-5aad308224f6
 * Link Button — https://app.subframe.com/4f62853ee923/library?component=Link+Button_a4ee726a-774c-4091-8c49-55b659356024
 * PRINT_DC_CoverPage — https://app.subframe.com/4f62853ee923/library?component=PRINT_DC_CoverPage_ed14470b-86a6-42cb-9cda-a1cadf903398
 */

import React from "react";
import { FeatherArrowUpRight } from "@subframe/core";
import { FeatherLayers2 } from "@subframe/core";
import { FeatherShare2 } from "@subframe/core";
import * as SubframeUtils from "../utils";
import { Dc_MetaIntro } from "./Dc_MetaIntro";
import { Dc_MetaIntro_ActionStack } from "./Dc_MetaIntro_ActionStack";
import { Dc_Popover_Card_CiteAs } from "./Dc_Popover_Card_CiteAs";
import { IconButton } from "./IconButton";
import { LinkButton } from "./LinkButton";

interface Print_Dc_CoverPageRootProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  imageSlot?: React.ReactNode;
  authorsSlot?: React.ReactNode;
  publishedText?: React.ReactNode;
  documentType?: React.ReactNode;
  title?: React.ReactNode;
  publicationDate?: React.ReactNode;
  translationLinkSlot?: React.ReactNode;
  className?: string;
}

const Print_Dc_CoverPageRoot = React.forwardRef<
  HTMLDivElement,
  Print_Dc_CoverPageRootProps
>(function Print_Dc_CoverPageRoot(
  {
    imageSlot,
    authorsSlot,
    publishedText,
    documentType,
    title,
    publicationDate,
    translationLinkSlot,
    className,
    ...otherProps
  }: Print_Dc_CoverPageRootProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "flex w-full flex-col items-center gap-12",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {imageSlot ? (
        <div className="flex w-full flex-col items-center gap-12">
          {imageSlot}
        </div>
      ) : null}
      <Dc_MetaIntro
        title="Digitale Demokratie – Lenken soziale Medien die Meinungsbildung?"
        paperTypeText="Short-Paper"
        publishedText={publishedText}
        versionLinkSlot={
          <Dc_MetaIntro.VersionLink versionLinkIcon={null}>
            {publicationDate}
          </Dc_MetaIntro.VersionLink>
        }
        translationLinkSlot={
          translationLinkSlot ? (
            <div className="flex items-start justify-between">
              {translationLinkSlot}
            </div>
          ) : null
        }
        moreInformationLinkSlot={
          <Dc_MetaIntro.MoreInformationLink>
            Weitere Informationen
          </Dc_MetaIntro.MoreInformationLink>
        }
        authorsSlot={
          authorsSlot ? (
            <div className="flex items-start gap-4">{authorsSlot}</div>
          ) : null
        }
        titleSlot={
          title ? (
            <span className="text-heading-1 font-heading-1 text-default-font">
              {title}
            </span>
          ) : null
        }
        typeSlot={
          <Dc_MetaIntro.TypeBadge>{documentType}</Dc_MetaIntro.TypeBadge>
        }
        isPrint={true}
        actionSlot={
          <>
            <Dc_MetaIntro_ActionStack.PdfDownloadButton>
              Download PDF
            </Dc_MetaIntro_ActionStack.PdfDownloadButton>
            <Dc_MetaIntro_ActionStack.ListenButton>
              Listen
            </Dc_MetaIntro_ActionStack.ListenButton>
            <Dc_MetaIntro_ActionStack.CiteButton
              popoverSlot={
                <Dc_MetaIntro_ActionStack.CitePopover
                  version="Version 1.0 vom 25.06.2024"
                  citation="Mahrt, M. (2024). Digitale Demokratie. Lenken soziale Medien die Meinungsbildung? (Reihe Fundamentals). Berlin: Weizenbaum-Institut."
                  doi="https://doi.org/10.34669/wi.fund/2.de.web"
                >
                  <Dc_Popover_Card_CiteAs
                    version="Version 1.0 vom 25.06.2024"
                    doi="https://doi.org/10.34669/wi.fund/2.de.web"
                    copySlot={
                      <Dc_Popover_Card_CiteAs.CopyInteraction
                        text="Copy citation"
                        copiedText="Copied"
                      />
                    }
                    linkSlot={
                      <LinkButton
                        size="small"
                        iconRight={<FeatherArrowUpRight />}
                      >
                        https://doi.org/10.34669/wi.fund/2.de.web
                      </LinkButton>
                    }
                    versionLinkSlot={
                      <>
                        <FeatherLayers2 className="text-caption font-caption text-subtext-color" />
                        <span className="text-caption-bold font-caption-bold text-subtext-color">
                          Version 1.0 vom 25.06.2024
                        </span>
                      </>
                    }
                  >
                    Mahrt, M. (2024). Digitale Demokratie. Lenken soziale Medien
                    die Meinungsbildung? (Reihe Fundamentals). Berlin:
                    Weizenbaum-Institut.
                  </Dc_Popover_Card_CiteAs>
                </Dc_MetaIntro_ActionStack.CitePopover>
              }
            >
              Cite
            </Dc_MetaIntro_ActionStack.CiteButton>
            <Dc_MetaIntro_ActionStack.DoiButton
              popoverSlot={
                <Dc_MetaIntro_ActionStack.DoiPopover
                  linkSlot={
                    <LinkButton
                      size="small"
                      iconRight={<FeatherArrowUpRight />}
                    >
                      https://doi.org/10.34669/wi.fund/2.de.web
                    </LinkButton>
                  }
                  interactionSlot={
                    <Dc_MetaIntro_ActionStack.CopyInteraction
                      text="Copy DOI"
                      copiedText="Copied"
                    />
                  }
                />
              }
            >
              DOI
            </Dc_MetaIntro_ActionStack.DoiButton>
          </>
        }
        shareSlot={
          <IconButton
            disabled={false}
            variant="neutral-secondary"
            size="large"
            icon={<FeatherShare2 />}
            loading={false}
          />
        }
      />
    </div>
  );
});

export const Print_Dc_CoverPage = Print_Dc_CoverPageRoot;
